import { updateComplianceItem } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FeaturedIcon,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { useNotification } from '../../../hooks/useNotification';

export const DeleteAssigneeModal = ({
  isOpen,
  onClose,
  assignee,
  filing,
  onSuccess,
  loggedInUserEmail,
}: {
  isOpen: boolean;
  onClose: () => void;
  assignee: { id: string; name: string; email: string };
  filing: TComplianceItem;
  onSuccess: () => void;
  loggedInUserEmail: string;
}) => {
  const { setNotification } = useNotification();

  const isDeletingAssignee = useToggle();

  const onDeleteAssignee = async () => {
    isDeletingAssignee.on();

    const res = await updateComplianceItem(filing.id, {
      assigneeIdsToRemove: [assignee.id],
    });

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to delete assignee',
      });
      isDeletingAssignee.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Assignee removed successfully.',
    });

    onSuccess();
    isDeletingAssignee.off();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideCloseIcon={isDeletingAssignee.value}
        preventCloseOnOutsideClick={isDeletingAssignee.value}
      >
        <DialogHeader className="flex justify-start items-start gap-lg border-b-0">
          <FeaturedIcon
            size="lg"
            variant="warning"
            icon={<Icons.AlertTriangle />}
          />
          <div className="flex flex-col gap-xs">
            <div className="txt-large-bold">Are you sure?</div>
            <p className="text-text-tertiary txt-small-regular">
              You are removing {assignee.name}
              {loggedInUserEmail === assignee.email
                ? ' (yourself) '
                : ''} from {filing.title}.
            </p>
          </div>
        </DialogHeader>

        <DialogFooter className="border-t-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
            disabled={isDeletingAssignee.value}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={onDeleteAssignee}
            disabled={isDeletingAssignee.value}
          >
            {isDeletingAssignee.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
